import type { AlgoliaProduct, TileType } from './types';
import type { FC, ReactNode, Ref } from 'react';

import dynamic from 'next/dynamic';
import { useState, useEffect, useMemo, useCallback } from 'react';

import { useProductTileAccessibility } from './hooks/useProductTileAccessibility';
import { S } from './styles';
import { TileImage } from './tileImage/TileImage';
import { FeatureFlag } from '~/components/shared/FeatureFlag';
import { ProductData } from '~/components/shared/recommendations/tile/productData/ProductData';
import { ProductDataAvailableColors } from '~/components/shared/recommendations/tile/productData/ProductDataAvailableColors';
import { ProductDataHoverCard } from '~/components/shared/recommendations/tile/productData/ProductDataHoverCard';
import { TileBaseProvider } from '~/components/shared/recommendations/tile/TileBaseContext';
import { useTileContext } from '~/components/shared/recommendations/tile/TileContext';
import { CodeFeatureFlag } from '~/config/code-feature-flags';
import type { AlgoliaProductHit } from '~/types/algolia';
import { CustomEventTypes } from '~/utilities/constants';
import { FIBER_RANKING_SCORE_THRESHOLD } from '~/utilities/constants/fiberRanking';
import { ALGOLIA_OBJECT_ID, PLP_NAVIGATED_PRODUCT } from '~/utilities/constants/localStorageKeys';
import { useQuickViewContext } from '~/utilities/context/dynamic/QuickViewContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { checkImageSwatchAvailability } from '~/utilities/helpers/findImageProfile';

const TileFiberRanking = dynamic(
  async () => (await import('./TileFiberRanking')).TileFiberRanking,
  { ssr: false }
);
const TileSignings = dynamic(async () => (await import('./TileSignings')).TileSignings, {
  ssr: false,
});
const ProductDataVariants = dynamic(
  async () => (await import('./productData/ProductDataVariants')).ProductDataVariants,
  { ssr: false }
);

type TileBaseProps = {
  tileContentOverlay?: ReactNode;
  ref?: Ref<HTMLAnchorElement>;
  tabIndex?: number;
  productInfo?: ReactNode;
};

export const TileBase: FC<TileBaseProps> = ({
  tileContentOverlay,
  ref,
  productInfo,
  tabIndex = 0,
}) => {
  const { siteType } = useAppContext();
  const { showAvailableColors, showQuickView } = CodeFeatureFlag[siteType];
  const { isQuickViewOpen } = useQuickViewContext();
  const { variants, product, showVariantSelector, tileType, aspectRatio } = useTileContext();
  const [imageHovered, setImageHovered] = useState(false);
  const [tileHovered, setTileHovered] = useState(false);
  const [variantHovered, setVariantHovered] = useState(-1);
  const [singleProduct, setSingleProduct] = useState<AlgoliaProduct>(product);
  const {
    configuration: { fiberRanking, displayFiberRankingOnProductTiles, productTileConfiguration },
  } = useStaticContext();
  const { rel, onLinkClick, wishlistAction, overrideImages, index, quickView, hideElements } =
    useTileContext();
  const showFiberRanking =
    displayFiberRankingOnProductTiles &&
    fiberRanking &&
    product.fiberRankingScore &&
    FIBER_RANKING_SCORE_THRESHOLD <= product.fiberRankingScore;

  /**
   * only gift cards don't have style variants, all other products do display style variants
   * even if it's the only style variant
   *
   * style variants can be disabled via prop, for example on recommendations carousels
   */
  const hasVariantArea = showAvailableColors ? showVariantSelector && !product.isGiftCard : false;

  // Determine if hover image should be displayed based on configuration
  const { showHoverImageOnPlp, showHoverImageOnRvp, showHoverImageOnRecommendations } =
    productTileConfiguration || {};

  const hoverConfigMap: Record<TileType, boolean> = {
    plp: showHoverImageOnPlp ?? false,
    rvp: showHoverImageOnRvp ?? false,
    recommendation: showHoverImageOnRecommendations ?? false,
    amplience: true,
    search: false,
    ctl: true,
    wishlist: true,
  };

  const shouldDisplayHover =
    (variantHovered >= 0 || imageHovered) && (hoverConfigMap[tileType] ?? false);

  const productData = JSON.stringify({
    baseProduct: singleProduct?.['baseProduct.code'],
    comingSoon: singleProduct?.comingSoon,
    staticCategoryPathIds: singleProduct?.staticCategoryPathIds,
    stockQuantity: singleProduct?.stockQuantity,
    mcc: singleProduct?.mccValue,
  });

  const filteredVariants = useMemo(
    () =>
      variants?.filter(
        ({ facetColorStyle, simplifiedImageJson }) =>
          !!facetColorStyle || checkImageSwatchAvailability(simplifiedImageJson)
      ) ?? [],
    [variants]
  );

  const hasVisibleVariants = hasVariantArea && filteredVariants.length > 1;

  const handleTileHover = useCallback(
    (state: boolean) => () => {
      if (!isQuickViewOpen || state) {
        setTileHovered(state);
      }
    },
    [isQuickViewOpen]
  );

  useEffect(() => {
    if (variantHovered >= 0 && variants?.length) {
      document.dispatchEvent(
        new CustomEvent(CustomEventTypes.PRODUCT_TILE_VARIANT_HOVERED, {
          detail: { variantHovered },
        })
      );

      setSingleProduct(variants[variantHovered] as AlgoliaProductHit);
    } else {
      setSingleProduct(product);
    }
  }, [variantHovered, product, variants]);

  const { tileAriaLabel } = useProductTileAccessibility();

  const providerValue = useMemo(
    () => ({
      singleProduct,
      filteredVariants,
      tileHovered,
      setVariantHovered,
    }),
    [filteredVariants, singleProduct, tileHovered]
  );

  if (!singleProduct.url) {
    return <></>;
  }

  return (
    <TileBaseProvider value={providerValue}>
      <S.Tile
        role="article"
        data-rbp={product.isRBP}
        data-testid="product-tile"
        data-hover-variant-area={hasVariantArea}
        data-has-variants={hasVisibleVariants}
        data-cs-capture=""
      >
        <S.TileLink
          ref={ref}
          rel={rel}
          aria-label={tileAriaLabel}
          href={singleProduct.url}
          data-testid="product-tile-link"
          data-slug={singleProduct['baseProduct.code']}
          data-product-data={productData}
          data-product-code={product.code}
          data-cs-capture=""
          tabIndex={tabIndex}
          onMouseEnter={handleTileHover(true)}
          onMouseLeave={handleTileHover(false)}
          onFocus={handleTileHover(true)}
          onBlur={handleTileHover(false)}
          onClick={event => {
            sessionStorage?.setItem(PLP_NAVIGATED_PRODUCT, product.code);
            sessionStorage?.setItem(ALGOLIA_OBJECT_ID, product.objectID);
            onLinkClick?.(event);
          }}
        >
          <S.ProductWrapper>
            {showFiberRanking && <TileFiberRanking wrapper={S.FiberSigningsWrapper} />}

            <S.RelativeElement
              data-testid="product-tile-image"
              data-cs-capture=""
              onMouseEnter={() => setImageHovered(true)}
              onMouseLeave={() => {
                if (!isQuickViewOpen) {
                  setImageHovered(false);
                }
              }}
            >
              <TileImage
                showHover={shouldDisplayHover}
                override={overrideImages?.image}
                overrideHovered={overrideImages?.hover}
                index={index}
              >
                {tileContentOverlay}
              </TileImage>

              <TileSignings className={showFiberRanking ? 'fiber-ranking-visible' : ''} />
            </S.RelativeElement>

            {(hideElements?.price && hideElements?.name) !== true && (
              <ProductData
                availableColors={
                  <FeatureFlag flag="showAvailableColors">
                    <ProductDataAvailableColors />
                  </FeatureFlag>
                }
                productInfo={productInfo}
              />
            )}
          </S.ProductWrapper>
        </S.TileLink>

        {hasVisibleVariants && (
          <S.VariantAreaWrapper>
            <ProductDataVariants />
          </S.VariantAreaWrapper>
        )}

        <S.Actions>
          <S.ImageAspectRatioWrapper $aspectRatio={aspectRatio}>
            {wishlistAction && <S.WishlistActionWrapper>{wishlistAction}</S.WishlistActionWrapper>}

            {quickView && showQuickView && (
              <S.QuickViewWrapper data-product-tile-quick-view="">{quickView}</S.QuickViewWrapper>
            )}
          </S.ImageAspectRatioWrapper>

          {hasVisibleVariants && <ProductDataHoverCard productInfo={productInfo} />}
        </S.Actions>
      </S.Tile>
    </TileBaseProvider>
  );
};
